import { useCallback, useEffect, useRef, useState } from 'react';
import { COLORS, SHADOW, btn } from '../utils/uiStyles';

let host = null;
let nextDialogId = 1;
let nextToastId = 1;

export function alertDialog(message, opts = {}) {
  return host
    ? host.open({ type: 'alert', message, ...opts })
    : Promise.resolve(window.alert(message));
}

export function confirmDialog(message, opts = {}) {
  return host
    ? host.open({ type: 'confirm', message, ...opts })
    : Promise.resolve(window.confirm(message));
}

export function promptDialog(message, defaultValue = '', opts = {}) {
  const hasDefaultValue = arguments.length >= 2;
  return host
    ? host.open({ type: 'prompt', message, defaultValue, ...opts })
    : Promise.resolve(hasDefaultValue ? window.prompt(message, defaultValue) : window.prompt(message));
}

export function toast(message, tone = 'success') {
  if (host) host.pushToast(message, tone);
  else window.alert(message);
}

const toneColor = {
  success: '#16a34a',
  error: '#dc2626',
  info: '#2563eb',
};

export default function AppDialogHost() {
  const [queue, setQueue] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [promptState, setPromptState] = useState({ id: null, value: '' });
  const inputRef = useRef(null);
  const okRef = useRef(null);

  const active = queue[0] || null;
  const promptValue = active?.type === 'prompt'
    ? (promptState.id === active.id ? promptState.value : String(active.defaultValue ?? ''))
    : '';

  const open = useCallback((dialog) => new Promise((resolve) => {
    const previousActive = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setQueue((prev) => [...prev, {
      id: nextDialogId++,
      danger: false,
      okText: '확인',
      cancelText: '취소',
      ...dialog,
      previousActive,
      resolve,
    }]);
  }), []);

  const pushToast = useCallback((message, tone = 'success') => {
    const id = nextToastId++;
    setToasts((prev) => [...prev.slice(-2), { id, message, tone, closing: false }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.map((item) => (item.id === id ? { ...item, closing: true } : item)));
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((item) => item.id !== id));
      }, 180);
    }, 2500);
  }, []);

  useEffect(() => {
    host = { open, pushToast };
    return () => {
      if (host?.open === open) host = null;
    };
  }, [open, pushToast]);

  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setTimeout(() => {
      if (active.type === 'prompt') {
        inputRef.current?.focus();
        inputRef.current?.select();
      } else {
        okRef.current?.focus();
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [active]);

  const resolveActive = useCallback((value) => {
    if (!active) return;
    const shouldRestoreFocus = queue.length <= 1;
    active.resolve(value);
    setQueue((prev) => {
      if (!prev.length || prev[0].id !== active.id) return prev;
      return prev.slice(1);
    });
    if (shouldRestoreFocus) {
      window.setTimeout(() => {
        try { active.previousActive?.focus?.(); } catch {}
      }, 0);
    }
  }, [active, queue.length]);

  const cancelActive = useCallback(() => {
    if (!active) return;
    resolveActive(active.type === 'alert' ? undefined : active.type === 'prompt' ? null : false);
  }, [active, resolveActive]);

  const okActive = useCallback(() => {
    if (!active) return;
    if (active.type === 'confirm') resolveActive(true);
    else if (active.type === 'prompt') resolveActive(promptValue);
    else resolveActive(undefined);
  }, [active, promptValue, resolveActive]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelActive();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      okActive();
    }
  };

  return (
    <>
      {active && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) cancelActive(); }}
          onKeyDown={onKeyDown}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--gw-overlay, #19191945)',
            zIndex: 2147483600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            boxSizing: 'border-box',
          }}
        >
          <div
            role={active.type === 'alert' ? 'alertdialog' : 'dialog'}
            aria-modal="true"
            aria-labelledby={active.title ? 'gw-dialog-title' : undefined}
            aria-describedby="gw-dialog-message-text"
            data-testid="gw-dialog"
            className="gw-dialog"
            style={{
              width: '520px',
              maxWidth: '100%',
              background: 'var(--gw-surface, #fff)',
              border: '1px solid var(--gw-border, #e5e5e2)',
              borderRadius: '10px',
              boxShadow: SHADOW.float,
              padding: '25px',
              boxSizing: 'border-box',
            }}
          >
            {active.title && (
              <h2
                id="gw-dialog-title"
                style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'var(--gw-text, #32312e)' }}
              >
                {active.title}
              </h2>
            )}
            <div
              id="gw-dialog-message-text"
              data-testid="gw-dialog-message"
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '13px',
                lineHeight: 1.8,
                color: 'var(--gw-secondary, #64635f)',
                padding: active.title ? '20px 0' : '0 0 20px',
                maxHeight: '65vh',
                overflow: 'auto',
              }}
            >
              {active.message}
            </div>
            {active.type === 'prompt' && (
              <input
                ref={inputRef}
                data-testid="gw-dialog-input"
                value={promptValue}
                onChange={(e) => setPromptState({ id: active.id, value: e.target.value })}
                style={{
                  width: '100%',
                  marginTop: 0,
                  marginBottom: '15px',
                  padding: '10px',
                  border: '1px solid var(--gw-border, #e5e5e2)',
                  borderRadius: '5px',
                  fontSize: '13px',
                  background: 'var(--gw-bg, #fff)',
                  color: 'var(--gw-text, #32312e)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: 0 }}>
              {active.type !== 'alert' && (
                <button
                  type="button"
                  data-testid="gw-dialog-cancel"
                  onClick={cancelActive}
                  style={btn('secondary')}
                >
                  {active.cancelText || '취소'}
                </button>
              )}
              {/* 위험 확인은 background 단축 속성으로 덮는다(primary 테두리가 투명이라 테두리는 그대로).
                  gw-btn 클래스를 붙이지 않는다 — hover !important 가 위험 단추를 파랑으로 바꾸지 않게. */}
              <button
                type="button"
                ref={okRef}
                data-testid="gw-dialog-ok"
                onClick={okActive}
                style={active.danger ? { ...btn('primary'), background: COLORS.danger } : btn('primary')}
              >
                {active.okText || '확인'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        style={{
          position: 'fixed',
          right: '24px',
          bottom: '24px',
          zIndex: 2147483590,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((item) => (
          <div
            key={item.id}
            role="status"
            aria-live="polite"
            data-testid="gw-toast"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              maxWidth: 'min(360px, 92vw)',
              background: '#111827',
              color: '#fff',
              borderRadius: '10px',
              padding: '10px 16px',
              fontSize: '13px',
              boxShadow: '0 10px 30px rgba(0,0,0,.18)',
              opacity: item.closing ? 0 : 1,
              transform: item.closing ? 'translateY(4px)' : 'translateY(0)',
              transition: 'opacity 180ms ease, transform 180ms ease',
              pointerEvents: 'auto',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '999px',
                background: toneColor[item.tone] || toneColor.info,
                flex: '0 0 auto',
              }}
            />
            <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.message}</span>
          </div>
        ))}
      </div>
    </>
  );
}
